import { unwrapResult } from "@reduxjs/toolkit";
import { getBundleOptions } from "src/modules/companies/redux";
import { createBundle } from "../redux/bundles.action";
import {
  SELECTION_EXISTING,
  SELECTION_NEW,
  describeSelection,
  findBundleById,
  matchedBundleFieldError,
} from "./bundle-modules";

// Why a resolve was refused, when the refusal came from here rather than from the server.
export const MATCHED_BUNDLE_ERROR = "MATCHED_BUNDLE";

// Tagged with a `code` rather than expressed as a `class extends Error`: the production build
// down-levels class syntax, and a down-levelled subclass of a built-in fails `instanceof`.
// A tag survives it, so callers can actually tell this rejection apart from a failed create.
export const isMatchedBundleError = (error) =>
  Boolean(error) && error.code === MATCHED_BUNDLE_ERROR;

const matchedBundleError = (matched) => {
  const error = new Error(matchedBundleFieldError(matched));
  error.code = MATCHED_BUNDLE_ERROR;
  // The bundle the switches describe, so a caller can name it without redoing the set math.
  error.matched = matched;
  return error;
};

// The bundle a company must end up on, given what the switch grid says. Every surface that
// edits a company's modules resolves it exactly the same way, so it is resolved here once:
//   unchanged      -> the bundle in the dropdown, exactly as before
//   another bundle -> REFUSE. See below.
//   nothing yet    -> create it FIRST and return the new id. A company is only ever assigned
//                     a real bundle id.
//
// All-or-nothing: the returned promise rejects when the create fails (duplicate module set,
// name taken, ...), so the caller's assign/approve never runs. The bundles middleware has
// already shown the server's message by then.
//
// `onBundleCreated({ id, moduleIds })` — called ONLY on the create path, and only once the
// create has been accepted, before the returned promise resolves and therefore before the
// caller attempts its assign/approve. It is how the form catches up with what this function
// just did: from the moment a bundle is created, that bundle — not the one the dropdown was
// showing when Save was pressed — is what the company is being put on, and the form has to
// say so. Two things depend on it, and both are the caller's to wire:
//   * the dropdown names the bundle actually about to be written, instead of naming the old
//     one for the whole of a create-and-assign;
//   * a save whose ASSIGN is then rejected can be retried. The retry re-resolves from the
//     form, and with the created bundle in it the selection reads UNCHANGED, so this returns
//     that id and the retry assigns it. Without it the retry reads SELECTION_EXISTING —
//     "these modules are a different bundle" — against the very bundle it made a moment ago,
//     and is refused for good.
// A rejected create never calls it, so the form, and the company, are left untouched.
export default function resolveBundleId({
  dispatch,
  bundleOptions,
  values,
  onBundleCreated,
}) {
  const selection = describeSelection(
    bundleOptions,
    values.bundle,
    values.moduleIds
  );

  // The invariant, enforced a second time on the path that actually writes. This function is
  // the only thing that decides which id gets assigned, and after this branch exactly two
  // ids can come out of it: `values.bundle`, which IS the dropdown's value, or the id of a
  // bundle just created under a name the admin typed into a field that says it will be
  // created and assigned. Both are consented to. It used to hand back
  // `selection.matched.id`, an id the admin had not chosen anywhere on screen — the one and
  // only way a save could commit a bundle the dropdown was not showing.
  //
  // Each surface blocks this state in its own validation, so this is unreachable through the
  // UI. It stays because a future caller that forgets to block must fail loudly rather than
  // silently reassign, and because "the resolver never returns an id the dropdown is not
  // showing" is a property that can be read off this one file.
  if (selection.status === SELECTION_EXISTING) {
    return Promise.reject(matchedBundleError(selection.matched));
  }

  if (selection.status === SELECTION_NEW) {
    return dispatch(createBundle({
      name: (values.newBundleName || "").trim(),
      description: "",
      moduleIds: values.moduleIds,
      // The create is the first half of one save, not an outcome the admin asked for, so it
      // does not get its own green toast here — the caller reports the assign/approve, which
      // is the thing that either happened or did not. (The Access Bundles > Add dialog
      // dispatches createBundle without this flag and still gets "Bundle Created
      // successfully".) A rejected create is still alerted: it IS the outcome of this save,
      // because nothing is assigned after it.
      suppressSuccessAlert: true,
    }))
      .then(unwrapResult)
      .then((created) => {
        if (!created || !created.id) {
          throw new Error("Bundle create returned no id");
        }
        // The bundle exists now. WAIT for the refreshed options before handing the id back,
        // so that the id this resolve returns — the one the assign writes AND the one
        // `onBundleCreated` puts in the dropdown — is the server's own. bundle-options casts
        // ids to int while a create response passes the driver's value through, and a "42"
        // where the option list says 42 is a Select that renders blank for the bundle the
        // company is being put on. Fired and forgotten, that was a race.
        //
        // What it is NOT is the thing that makes a retry work. That is `onBundleCreated`
        // below, which moves the FORM onto the created bundle: a selection whose bundle is
        // not in the option list reads UNCHANGED too, so even a refresh that failed leaves
        // the retry assigning this id rather than creating the bundle twice.
        //
        // The wait cannot turn a successful create into a failure: a rejected thunk still
        // RESOLVES its dispatch promise.
        //
        // Nor may a failed refresh be REPORTED as a failure. The create has already
        // succeeded and the assign has not been attempted yet, so alerting it would put a
        // sticky red toast in front of an admin whose save then went through perfectly. It
        // is marked silent — and only this dispatch is. A first load of bundle-options,
        // which the admin IS waiting on, still alerts.
        return dispatch(getBundleOptions({ suppressErrorAlert: true }))
          .then((action) => {
            const refreshed = getBundleOptions.fulfilled.match(action)
              ? action.payload
              : bundleOptions;
            const live = findBundleById(refreshed, created.id);
            const id = live ? live.id : created.id;
            if (onBundleCreated) {
              try {
                onBundleCreated({
                  id,
                  // What the new bundle grants. The request's own set is the fallback — it
                  // is what the server was asked to store, and it is what the switches on
                  // screen are already showing — so a create response carrying nothing but
                  // an id, or a refresh that failed, still leaves the form coherent.
                  moduleIds: (
                    (live && live.moduleIds)
                    || created.moduleIds
                    || values.moduleIds
                    || []
                  ).slice(),
                });
              } catch (formError) {
                // Moving the form is for the NEXT attempt. It cannot un-create the bundle,
                // and it must not abort THIS save before the assign the admin is waiting on
                // — a create that succeeded and then assigned nothing, silently, is the one
                // outcome this whole path exists to prevent.
                console.warn("Could not move the form onto the created bundle", formError);
              }
            }
            return id;
          });
      });
  }

  return Promise.resolve(values.bundle);
}
